import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveToolsSection = ({ onToolLaunch }) => {
  const tools = [
    {
      id: 'ai-readiness',
      title: 'AI Readiness Assessment',
      description: 'Comprehensive evaluation of your organization\'s readiness for AI transformation and open-source adoption.',
      icon: 'CheckSquare',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      features: [
        'Technical infrastructure evaluation',
        'Team capability assessment',
        'Risk and compliance analysis',
        'Personalized recommendations'
      ],
      duration: '15-20 minutes',
      completions: '8.2K',
      rating: 4.9
    },
    {
      id: 'roi-calculator',
      title: 'Open-Source AI ROI Calculator',
      description: 'Calculate potential cost savings and ROI from migrating to open-source AI solutions versus proprietary alternatives.',
      icon: 'Calculator',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      features: [
        'Cost comparison analysis',
        'Migration timeline estimation',
        'Risk mitigation planning',
        'Executive summary report'
      ],
      duration: '10-15 minutes',
      completions: '12.1K',
      rating: 4.8
    },
    {
      id: 'vendor-risk',
      title: 'Vendor Lock-in Risk Analyzer',
      description: 'Assess your current vendor dependencies and identify potential risks and mitigation strategies.',
      icon: 'Shield',
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
      features: [
        'Dependency mapping',
        'Risk severity scoring',
        'Exit strategy planning',
        'Alternative solution recommendations'
      ],
      duration: '12-18 minutes',
      completions: '6.7K',
      rating: 4.7
    },
    {
      id: 'implementation-planner',
      title: 'AI Implementation Timeline Generator',
      description: 'Create a customized implementation roadmap based on your organization\'s specific requirements and constraints.',
      icon: 'Calendar',
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      features: [
        'Phase-by-phase planning',
        'Resource allocation guidance',
        'Milestone tracking',
        'Risk checkpoint identification'
      ],
      duration: '20-25 minutes',
      completions: '4.9K',
      rating: 4.9
    }
  ];

  return (
    <section className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Interactive Assessment Tools
        </h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Get personalized insights and actionable recommendations with our comprehensive assessment tools
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        {tools?.map((tool) => (
          <div
            key={tool?.id}
            className="bg-white rounded-xl shadow-executive border border-gray-200 hover:shadow-executive-lg transition-all duration-300 group overflow-hidden"
          >
            {/* Gradient Header */}
            <div className={`h-1 bg-gradient-to-r ${tool?.color}`}></div>
            
            <div className="p-8">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className={`p-4 ${tool?.bgColor} rounded-xl group-hover:scale-105 transition-transform duration-200`}>
                  <Icon name={tool?.icon} size={32} className={tool?.iconColor} />
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-1 text-sm text-text-secondary mb-1">
                    <Icon name="Star" size={16} className="text-yellow-500" />
                    <span>{tool?.rating}</span>
                  </div>
                  <div className="text-xs text-text-secondary">
                    {tool?.completions} completed
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-text-primary mb-3 group-hover:text-brand-primary transition-colors duration-200">
                  {tool?.title}
                </h3>
                <p className="text-text-secondary mb-4">
                  {tool?.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-4">
                  {tool?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-text-secondary">
                      <Icon name="Check" size={16} className="mr-3 text-brand-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Duration */}
                <div className="flex items-center text-sm text-text-secondary">
                  <Icon name="Clock" size={16} className="mr-2" />
                  <span>Estimated time: {tool?.duration}</span>
                </div>
              </div>

              {/* Action */}
              <Button
                variant="default"
                fullWidth
                className="bg-brand-primary hover:bg-brand-primary/90"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => onToolLaunch(tool)}
              >
                Start Assessment
              </Button>
            </div>
          </div>
        ))}
      </div>
      {/* Additional Info */}
      <div className="mt-8 bg-brand-surface rounded-lg p-6">
        <div className="flex items-center justify-center space-x-8 text-center">
          <div>
            <div className="text-2xl font-bold text-brand-primary mb-1">31K+</div>
            <div className="text-sm text-text-secondary">Assessments Completed</div>
          </div>
          <div className="h-12 w-px bg-gray-300"></div>
          <div>
            <div className="text-2xl font-bold text-brand-primary mb-1">4.8</div>
            <div className="text-sm text-text-secondary">Average Rating</div>
          </div>
          <div className="h-12 w-px bg-gray-300"></div>
          <div>
            <div className="text-2xl font-bold text-brand-primary mb-1">15 min</div>
            <div className="text-sm text-text-secondary">Average Completion</div>
          </div>
          <div className="h-12 w-px bg-gray-300"></div>
          <div>
            <div className="text-2xl font-bold text-brand-primary mb-1">92%</div>
            <div className="text-sm text-text-secondary">Find Results Valuable</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveToolsSection;